// The profile photo is prepared in the browser: the original file is never
// uploaded. A JPEG / PNG / WebP up to 10 MB is decoded (EXIF orientation
// applied), cropped to the centre square and scaled to 256×256, then encoded
// as WebP (JPEG where the browser cannot encode WebP) under 60 KB.

export const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const AVATAR_MAX_INPUT = 10 * 1024 * 1024;
const SIDE = 256;
const MIN_SIDE = 64;
const MAX_OUTPUT = 60 * 1024;

export type AvatarInputCode = 'type' | 'size' | 'small' | 'decode';
export class AvatarInputError extends Error {
  constructor(public code: AvatarInputCode) { super(code); }
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

const toDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

async function decode(file: File): Promise<CanvasImageSource & { width: number; height: number; close?: () => void }> {
  if ('createImageBitmap' in window) return createImageBitmap(file);
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return Object.assign(img, { width: img.naturalWidth, height: img.naturalHeight });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A data URL of the optimized square photo, or AvatarInputError. */
export async function prepareAvatar(file: File): Promise<string> {
  if (!AVATAR_TYPES.includes(file.type)) throw new AvatarInputError('type');
  if (file.size > AVATAR_MAX_INPUT) throw new AvatarInputError('size');
  let source: Awaited<ReturnType<typeof decode>>;
  try { source = await decode(file); } catch { throw new AvatarInputError('decode'); }
  const crop = Math.min(source.width, source.height);
  if (crop < MIN_SIDE) { source.close?.(); throw new AvatarInputError('small'); }
  const side = Math.min(SIDE, crop);
  const draw = (background?: string) => {
    const canvas = document.createElement('canvas');
    canvas.width = side; canvas.height = side;
    const ctx = canvas.getContext('2d')!;
    if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, side, side); }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, (source.width - crop) / 2, (source.height - crop) / 2, crop, crop, 0, 0, side, side);
    return canvas;
  };
  try {
    const canvas = draw();
    let flat: HTMLCanvasElement | null = null;
    for (const quality of [0.86, 0.76, 0.64, 0.5]) {
      let blob = await toBlob(canvas, 'image/webp', quality);
      if (!blob || blob.type !== 'image/webp') {
        flat ??= draw('#16181d'); // JPEG has no transparency
        blob = await toBlob(flat, 'image/jpeg', quality);
      }
      if (blob && blob.size <= MAX_OUTPUT) return await toDataUrl(blob);
    }
    throw new AvatarInputError('size');
  } finally {
    source.close?.();
  }
}
